import {
  TextField,
  IconButton,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { SearchStyles } from "./CustomerTilesPanelStyles";
import ClearIcon from "@mui/icons-material/Clear";
import { useRef, useState } from "react";
import { useKey, useMount } from "react-use";
import { WHICH_CONFIGS_OPTIONS, WhichConfigsType } from "./customerTilesUtils";
import { getIsTouchDevice } from "utils/getIsTouchDevice";
const SHORTCUT_KEY_SEARCH = "/";
const SHORTCUT_KEY_SEARCH_CONFIGS = "c";
export function SearchCustomers({
  label,
  searchTerm,
  handleSearchChange,
  handleClearSearch,
  searchConfigsTerm,
  handleSearchConfigsChange,
  whichConfigsToSearch,
  setWhichConfigsToSearch,
  showMoreInfo,
}: {
  label?: string;
  searchTerm: string;
  handleSearchChange: (e: any) => void;
  handleClearSearch: () => void;
  searchConfigsTerm?: string;
  handleSearchConfigsChange?: (searchTerm: string) => void;
  whichConfigsToSearch?: WhichConfigsType[];
  setWhichConfigsToSearch?: React.Dispatch<
    React.SetStateAction<WhichConfigsType[]>
  >;
  showMoreInfo?: boolean;
}) {
  const [isSearchFocused, setIsSearchFocused] = useState(true);
  const [isSearchConfigsFocused, setIsSearchConfigsFocused] = useState(false);
  const isAnySearchFocused = isSearchFocused || isSearchConfigsFocused;

  const inputRef = useRef<HTMLInputElement>(null);
  useKey(
    SHORTCUT_KEY_SEARCH,
    () => {
      if (isAnySearchFocused) {
        return;
      }
      inputRef.current?.focus();
    },
    {},
    [isAnySearchFocused]
  );

  const inputRefSearchConfigs = useRef<HTMLInputElement>(null);
  useKey(
    SHORTCUT_KEY_SEARCH_CONFIGS,
    e => {
      if (e.ctrlKey || e.metaKey || isAnySearchFocused) {
        return;
      }
      inputRefSearchConfigs.current?.focus();
    },
    {},
    [isAnySearchFocused]
  );

  useKey("Escape", handleClearSearch);
  const isTouchDevice = getIsTouchDevice();
  useMount(() => {
    // don't autofocus on touch device (brings up the on-screen keyboard)
    if (isTouchDevice) {
      return;
    }
    inputRef.current?.select();
    inputRef.current?.focus();
  });

  const whichConfigsText = whichConfigsToSearch?.join("+");

  return (
    <SearchStyles>
      <div className="inputWrapper">
        <TextField
          variant="outlined"
          type="text"
          inputProps={{
            autoFocus: !isTouchDevice, // don't autofocus on touch device (brings up the on-screen keyboard)
            onFocus: e => {
              setIsSearchFocused(true);
              e.currentTarget.select();
              e.currentTarget.focus();
            },
            onClick: e => {
              setIsSearchFocused(true);
              e.currentTarget.select();
              e.currentTarget.focus();
            },
            onBlur: () => {
              setIsSearchFocused(false);
            },
          }}
          inputRef={inputRef}
          value={searchTerm}
          onChange={e => {
            if (e.target.value === SHORTCUT_KEY_SEARCH) {
              return;
            }
            handleSearchChange(e);
          }}
          label={
            isSearchFocused
              ? (label ?? `Search customers...`)
              : `Press "/" to search${showMoreInfo ? " customers" : ""}...`
          }
        />
        {searchTerm !== "" && (
          <IconButton onClick={handleClearSearch} size="large">
            <ClearIcon />
          </IconButton>
        )}
      </div>
      {showMoreInfo ? (
        <div className="more-info-search-row">
          <div className="inputWrapper">
            <TextField
              style={{ width: 360 }}
              variant="outlined"
              type="text"
              inputProps={{
                onFocus: e => {
                  setIsSearchConfigsFocused(true);
                  e.currentTarget.select();
                  e.currentTarget.focus();
                },
                onClick: e => {
                  setIsSearchConfigsFocused(true);
                  e.currentTarget.select();
                  e.currentTarget.focus();
                },
                onBlur: () => {
                  setIsSearchConfigsFocused(false);
                },
              }}
              inputRef={inputRefSearchConfigs}
              value={searchConfigsTerm}
              onChange={e => {
                if (e.target.value === SHORTCUT_KEY_SEARCH_CONFIGS) {
                  return;
                }
                handleSearchConfigsChange?.(e.target.value);
              }}
              label={
                isSearchConfigsFocused
                  ? (label ?? `Search ${whichConfigsText}...`)
                  : `Press "${SHORTCUT_KEY_SEARCH_CONFIGS}" to search configs`
              }
            />
          </div>
          {whichConfigsToSearch ? (
            <div>
              <Select
                label="Which Configs"
                multiple={true}
                value={whichConfigsToSearch ?? []}
                renderValue={selected => {
                  return (selected as WhichConfigsType[]).join("+");
                }}
                onChange={(event: SelectChangeEvent<WhichConfigsType[]>) => {
                  const configs = event.target.value;
                  if (Array.isArray(configs)) {
                    setWhichConfigsToSearch?.(configs);
                  }
                }}
              >
                {WHICH_CONFIGS_OPTIONS.map(config => (
                  <MenuItem key={config} value={config}>
                    <Checkbox
                      checked={whichConfigsToSearch?.includes(config)}
                    />
                    <ListItemText primary={config} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          ) : null}
        </div>
      ) : null}
    </SearchStyles>
  );
}
//  TODO switch toggles to checkboxes

// TODO monthly reports -> who has reports in READY status (and all statuses) (current month only!) (use the month + year column)

// TODO ask sophia marisha about monthly indicators

// TODO future ticket -> put hubspot link in customer info

// TODO dropdown -> search checkboxes for which configs to search
